// Señales de INTENSIDAD de uso del operador (Item B2-emisión) + contador de core-actions
// per-usuario que consume el feedback-por-uso del preview (Item E#3).
//
// Dos responsabilidades, una sola superficie para no duplicar el concepto de "acción core":
//   1. B2 — emitir a GA4 (reusando el sink de analytics) cada acción de edición de menú con
//      `core_action=true` + `by_user_id`, y un heartbeat de minutos activos. Permite al dashboard
//      identificar al "heavy daily operator" y ponderar después el feedback por intensidad.
//   2. E#3 — llevar un contador per-usuario (per-tab, en memoria) de core-actions para disparar
//      el prompt de feedback OMITIBLE tras N acciones reales. NO se persiste como variante: es
//      una señal de uso, no estado de rollout.
//
// SIN PII: solo `by_user_id` (el id de usuario que el resto de telemetría ya emite) + el tipo de
// acción. Nunca nombre, email ni contenido del producto. Si el sink está apagado (no-prod / sin
// tracking), `ga4Event` es no-op y nada rompe.

import { ga4Event } from '@utils/analytics/ga4';
import { getStoreSnapshot } from './store';
import { isPreviewActive } from './preview';
import { MENU_FEATURE_KEY } from './constants';

// Tipos de acción core del menú. Estables (viajan en el payload GA4, no se traducen).
export type CoreActionType = 'product_create' | 'product_edit' | 'product_reorder';

// ¿La versión NUEVA (V2) del menú está en pantalla para este usuario? = variante treatment
// adoptada por el comercio O vista previa per-usuario activa. Solo en ese caso una core-action
// es "uso de V2": las acciones en V1 (control sin preview) NO deben contar ni emitir la señal de
// intensidad de V2 (Seer 14512239/1 — evita inflar core_action_count con uso de V1, que
// corrompería la ponderación del feedback por intensidad y el slice por variante).
function isMenuV2OnScreen(): boolean {
  const variant = getStoreSnapshot(MENU_FEATURE_KEY).entry.variant;
  return variant === 'treatment' || isPreviewActive(MENU_FEATURE_KEY);
}

// Nombre de evento GA4 (snake_case, coherente con el resto del catálogo de rollout).
const GA4_CORE_ACTION_EVENT = 'rollout_core_action';
const GA4_HEARTBEAT_EVENT = 'rollout_active_heartbeat';

// Cadencia del heartbeat de minutos activos: una marca por minuto de actividad real (no por
// tiempo de pestaña abierta inactiva). El consumidor llama `pingActivity()` ante interacción;
// solo emitimos si pasó al menos un minuto desde el último latido.
const HEARTBEAT_INTERVAL_MS = 60_000;

function byUserId(): string {
  if (typeof window === 'undefined') return '';
  try {
    return window.localStorage.getItem('id_user') ?? '';
  } catch {
    return '';
  }
}

// ── Contador de core-actions per-usuario (per-tab, en memoria) ─────────────────────────
// Keyed por user_id para que un cambio de usuario en la misma pestaña (multisucursal) no
// herede el conteo del anterior. No se persiste: el prompt de feedback es per-sesión-de-uso.
const coreActionCounts = new Map<string, number>();
type CountListener = (count: number) => void;
const countListeners = new Set<CountListener>();

// Notifica PULL, no PUSH: no difundimos el valor del contador (que es del usuario que acaba de
// actuar). Cada listener vuelve a leer getCoreActionCount() para SU usuario activo, evitando que
// el listener del usuario B reciba el conteo del usuario A en una carrera (Seer 14512239/0).
function notifyCount(): void {
  const current = coreActionCounts.get(byUserId()) ?? 0;
  countListeners.forEach((l) => l(current));
}

// Suscribe al contador de core-actions del usuario activo. Devuelve unsubscribe.
export function subscribeCoreActionCount(listener: CountListener): () => void {
  countListeners.add(listener);
  return () => {
    countListeners.delete(listener);
  };
}

export function getCoreActionCount(): number {
  return coreActionCounts.get(byUserId()) ?? 0;
}

// ── Heartbeat de minutos activos ───────────────────────────────────────────────────────
let lastHeartbeatAt = 0;

// Marca actividad. Emite un latido a GA4 a lo sumo una vez por minuto. Idempotente dentro de
// la ventana: llamarlo en ráfaga no infla el conteo de minutos activos.
export function pingActivity(): void {
  const now = Date.now();
  if (now - lastHeartbeatAt < HEARTBEAT_INTERVAL_MS) return;
  lastHeartbeatAt = now;
  ga4Event(GA4_HEARTBEAT_EVENT, { by_user_id: byUserId() });
}

// ── Registro de una core-action ─────────────────────────────────────────────────────────
// Lo llaman los hooks de menú (compartidos V1/V2) en create/edit/reorder de producto. Solo
// cuenta como "uso de V2" si la versión nueva está EN PANTALLA (treatment o preview): en V1 puro
// no emite señal de intensidad de V2 ni incrementa el contador (Seer 14512239/1). El heartbeat de
// minutos activos SÍ se marca siempre (mide actividad del operador, no de una versión concreta).
export function recordMenuCoreAction(action: CoreActionType): void {
  // El heartbeat es agnóstico de versión: cualquier edición es "operador activo".
  pingActivity();

  // De aquí en adelante, solo si V2 está en pantalla: la señal de intensidad y el contador E#3
  // miden uso de la versión NUEVA, no de la anterior.
  if (!isMenuV2OnScreen()) return;

  const userId = byUserId();

  // B2 — señal a GA4. El `rollout_variant` ya viaja como user_property global (applyFeatureVersionTags),
  // así el dashboard puede slicear por variante sin que lo repitamos aquí.
  ga4Event(GA4_CORE_ACTION_EVENT, { core_action: true, action, by_user_id: userId });

  // E#3 — contador per-usuario (per-tab) que dispara el prompt de feedback por uso.
  const next = (coreActionCounts.get(userId) ?? 0) + 1;
  coreActionCounts.set(userId, next);
  notifyCount();
}

// Solo para tests: limpia el estado de módulo entre casos.
export function __resetUsageSignalsForTests(): void {
  coreActionCounts.clear();
  countListeners.clear();
  lastHeartbeatAt = 0;
}
